import { Alert } from "@cmsgov/ds-medicare-gov";
import { AxiosResponse } from "axios";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { CreateUnlockAccountResponse } from "../hooks/useCreateAccountApi";
import { useConversion } from '../../common/hooks/use.analytics';
import { getErrorContentFns } from "../functions/errorAlertContent/getErrorContentFns";
import { InvalidField } from "../functions/errorAlertContent/getAlertContent";
import Div from "./div";
import { ForgotPasswordErrorsResponse } from "../hooks/useForgotPasswordApi";
import { VerifyAccountResponse } from "../hooks/useVerifyAccountApi";
import { LoginResponse, MfaSendCodeResponse } from "../hooks/useLoginApi";
import { scrollIntoViewAndFocus } from "../functions/scrollIntoViewAndFocus";
import { MfaResponse, MfaEnrollStep1Response, MfaEnrollStep2Response, MfaDeleteErrorResponse } from "../../mfa/hooks/useMfaApi";
import { alertImpression } from "../analytics";

export type FlowName =
    'Login' |
    'CreateAccount1' |
    'UnlockAccount1' |
    'ForgotPassword1' |
    'ForgotPassword2' |
    'ForgotPassword3' |
    'ForgotUsername' |
    'ForgotUsernameAndPassword' |
    'FindMyAccount' |
    'MfaEnrollStep1Sms' |
    'MfaEnrollStep1Voice' |
    'MfaEnrollStep1Email' |
    'MfaEnrollStep2Sms' |
    "MfaEnrollStep2Voice" |
    "MfaEnrollStep2Email" |
    "MfaDeleteMethod" |
    "MfaVerifyPassword" |
    'MfaLogin'; // for login MFA pages other than the initial login page

// all field names used anywhere in the app
export type FieldName =
    'confirmPassword' |
    'coverageStartDate' |
    'dateOfBirth' |
    'email' |
    'lastName' |
    'mbi' |
    'newPassword' |
    'password' |
    'secretAnswer' |
    'username' |
    'zipCodeOrCity' |
    'field' //used as a dummy value for alerts that don't use field refs.

// used to name ref properties for a given set of FieldNames
type FieldRef<name extends FieldName> = `${Extract<FieldName, name>}Ref`;
// helper type used to picking fields used by a flow
type UseFields<Fields extends FieldName> = Extract<FieldName, Fields>;

export type FlowDef<Name extends FlowName, ApiResponseType, Fields extends FieldName> = {
    /** name of the flow - this is used to determine which ref props to use */
    name: Name
    /** current API response - pass in null to start and at the start of every API call */
    apiResponse: ApiResponseType | null
    /** used so the error alert can fire off analytics error events */
    conversion: ReturnType<typeof useConversion> | null
    /** current invalid fields - this should be an empty array at the start of every submit. Use `null` for flows that don't use field errors */
    invalidFields: InvalidField<Fields>[] | null
    /** callback for when API errors return invalid fields that need to show inline errors */
    onInvalidField: (invalidFields: InvalidField<Fields>[]) => void
    className?: string
    parentHeading: string
} & Record<FieldRef<Fields>, HTMLElement | null>;

export type LoginFields = UseFields<'username' | 'password'>;
export type LoginAlertProps = FlowDef<
    'Login',
    AxiosResponse<LoginResponse> | AxiosResponse<MfaSendCodeResponse>,
    LoginFields
>  & {
    loginFailCount: number
};

export type CreateAccount1Fields = UseFields<'mbi' | 'coverageStartDate'>;
export type CreateAccount1AlertProps = FlowDef<
    'CreateAccount1',
    AxiosResponse<CreateUnlockAccountResponse>,
    CreateAccount1Fields
>;

export type UnlockAccount1Fields = UseFields<'mbi' | 'coverageStartDate'>;
export type UnlockAccount1AlertProps = FlowDef<
    'UnlockAccount1',
    AxiosResponse<CreateUnlockAccountResponse>,
    UnlockAccount1Fields
>;

export type ForgotPassword1Fields = UseFields<'username' | 'mbi' | 'lastName' | 'dateOfBirth'>;
export type ForgotPassword1AlertProps = FlowDef<
    'ForgotPassword1',
    ForgotPasswordErrorsResponse,
    ForgotPassword1Fields
>;

export type ForgotPassword2Fields = UseFields<'secretAnswer'>
export type ForgotPassword2AlertProps = FlowDef<
    'ForgotPassword2',
    ForgotPasswordErrorsResponse,
    ForgotPassword2Fields
> & {
    incorrectAnswerCount: number
};

export type ForgotPassword3Fields = UseFields<'newPassword' | 'confirmPassword'>;
export type ForgotPassword3AlertProps = FlowDef<
    'ForgotPassword3',
    ForgotPasswordErrorsResponse,
    ForgotPassword3Fields
>;

export type ForgotUsernameFields = UseFields<'mbi' | 'lastName' |'dateOfBirth'>;
export type ForgotUsernameAlertProps = FlowDef<
    'ForgotUsername' | 'ForgotUsernameAndPassword',
    AxiosResponse<VerifyAccountResponse>,
    ForgotUsernameFields
>;

export type FindMyAccountFields = ForgotUsernameFields;
export type FindMyAccountAlertProps = FlowDef<
    'FindMyAccount',
    AxiosResponse<VerifyAccountResponse>,
    FindMyAccountFields
>;

export type MfaFields = UseFields<'field'>;
export type MfaStepProps = FlowDef<
    'MfaEnrollStep1Sms' | 'MfaEnrollStep1Voice' | 'MfaEnrollStep1Email' | 'MfaEnrollStep2Sms' | "MfaEnrollStep2Voice" | "MfaEnrollStep2Email" | "MfaDeleteMethod",
    AxiosResponse<MfaResponse<MfaEnrollStep2Response | MfaEnrollStep1Response>> | null,
    'field'
>;

export type MfaDeleteProps = FlowDef<
    'MfaDeleteMethod',
    AxiosResponse<MfaDeleteErrorResponse>,
    'field'
>;

export type MfaSendAlertProps = FlowDef<
    'MfaLogin',
    AxiosResponse<LoginResponse> | AxiosResponse<MfaSendCodeResponse>,
    'field'
> & {
    loginFailCount: number
};

export type PasswordFields = UseFields<'password'>;
export type MfaVerifyPasswordProps = FlowDef<
    'MfaVerifyPassword',
    {},
    PasswordFields
> & {
    errorKey?: string
};

export type SmartErrorAlertProps =
    LoginAlertProps |
    CreateAccount1AlertProps |
    UnlockAccount1AlertProps |
    ForgotPassword1AlertProps |
    ForgotPassword2AlertProps |
    ForgotPassword3AlertProps |
    ForgotUsernameAlertProps |
    FindMyAccountAlertProps |
    MfaStepProps |
    MfaDeleteProps |
    MfaSendAlertProps |
    MfaVerifyPasswordProps

export const SmartErrorAlert = (props: SmartErrorAlertProps) => {
    const [errState, setErrState] = useState<string | null>(null);
    const [apiInvalidFields, setApiInvalidFields] = useState(false);
    const [alertRef, setAlertRef] = useState<HTMLElement | null>(null);
    const [showAlert, setShowAlert] = useState(false);

    const [header, setHeader] = useState<string>();
    const [jsxHeader, setJsxHeader] = useState<ReactNode>();
    const [body, setBody] = useState<ReactNode>();
    const [analyticsErrString, setAnalyticsErrString] = useState('');

    const {
        apiResponse,
        invalidFields,
        conversion,
        name,
        className
    } = props;

    // get the error content functions for the given flow
    const contentGenerator = getErrorContentFns(name);

    const failValidation = contentGenerator.isIntermediateStep ? conversion?.failValidationIntermediate : conversion?.failValidation;
    const failAPI = contentGenerator.isIntermediateStep ? conversion?.failAPIIntermediate : conversion?.failAPI;

    const onApiResponseChange = useCallback((response) => {
        let newErrorState: string | null;
        if (props.name === 'ForgotPassword2') {
            newErrorState = contentGenerator.getErrState(response, props.incorrectAnswerCount);
        } else if (props.name === 'Login' || props.name === 'MfaLogin') {
            newErrorState = contentGenerator.getErrState(response, props.loginFailCount);
        } else if (props.name === 'MfaVerifyPassword') {
            newErrorState = contentGenerator.getErrState({}, props.errorKey);
        } else {
            newErrorState = contentGenerator.getErrState(response);
        }
        setErrState(newErrorState);
        const apiInvalidFields = contentGenerator.getApiInvalidFields(response);

        // notify parent component of any API errors related to invalid fields
        // @ts-ignore - invalidFields is typed in a generic way, so TS doesn't think it can be used for each specific flow
        props.onInvalidField(apiInvalidFields);

        setApiInvalidFields(!!apiInvalidFields.length);
        // invalid fields from API response
        if (!newErrorState && apiInvalidFields.length) {
            const content = contentGenerator.getInvalidFieldContent(props);
            setHeader(content.header);
            setJsxHeader(content.headerJSX);
            setBody(content.body);
            if (failAPI) {
                failAPI(content.analyticsErr);
            }
        }
    }, [contentGenerator, failAPI, props]);

    const lastApiResponse = useRef<SmartErrorAlertProps['apiResponse']>(null);
    // set err state based on apiResponse
    useEffect(() => {
        if (apiResponse === lastApiResponse.current) {
            return;
        }
        lastApiResponse.current = apiResponse;
        onApiResponseChange(apiResponse);
    }, [apiResponse, onApiResponseChange]);


    const setErrStateContent = useCallback((err) => {
        const content = contentGenerator.getApiAlertContent(err);
        setHeader(content.header);
        setJsxHeader(content.headerJSX);
        setBody(content.body);
        setAnalyticsErrString(content.analyticsErr);
        if (failAPI) {
            failAPI(content.analyticsErr);
        }
    }, [failAPI, contentGenerator]);

    // update alert content based on errState
    const lastErrState = useRef<string | null>(null);
    useEffect(() => {
        if (errState === lastErrState.current) {
            return;
        }
        lastErrState.current = errState;

        if (errState) {
            setErrStateContent(errState);
        }
    }, [errState, setErrStateContent]);


    const invalidFieldUpdate = useCallback(() => {
        const content = contentGenerator.getInvalidFieldContent(props);
        setHeader(content.header);
        setJsxHeader(content.headerJSX);
        setBody(content.body);
        setAnalyticsErrString(content.analyticsErr);
        if (failValidation) {
            failValidation(content.analyticsErr);
        }
    }, [failValidation, contentGenerator, props]);

    // update alert based on invalid fields
    const lastInvalidFields = useRef<InvalidField<any>[] | null>([]);
    useEffect(() => {
        if (invalidFields === lastInvalidFields.current) {
            return;
        }
        lastInvalidFields.current = invalidFields;

        if (invalidFields?.length) {
            invalidFieldUpdate();
        }
    }, [invalidFields, invalidFieldUpdate]);

    useEffect(() => {
        const hasErr = Boolean(errState || invalidFields?.length || apiInvalidFields);
        setShowAlert(hasErr);
        if (hasErr) {
            scrollIntoViewAndFocus(alertRef);
        }
    }, [errState, invalidFields, apiInvalidFields, alertRef]);

    // analytics events
    useEffect(() => {
        if (!showAlert) {
            return;
        }
        alertImpression({
            heading: header || '',
            alertType: 'error',
            text: analyticsErrString
        });
    }, [showAlert, analyticsErrString, header]);

    return <>
        {showAlert &&
            <Div isRow>
                <Div cols={12} colsMD={8}>
                    <Alert
                        className={className || "ds-u-margin-top--3"}
                        heading={header}
                        role="alert"
                        variation="error"
                        alertRef={setAlertRef}
                    >
                        {jsxHeader &&
                            <h2 className="ds-c-alert__heading">
                                {jsxHeader}
                            </h2>
                        }
                        {body}
                    </Alert>
                </Div>
            </Div>
        }
    </>;
};